import { RootState } from "store";
import { AppThunk, createAppAsyncThunk } from "appThunk";
import {
    PayloadAction,
    createSelector,
    createSlice
} from "@reduxjs/toolkit";

import { MonthlyCosts, loadMonthlyCosts } from "./monthlyCosts";
import { MonthlyRevenue, loadMonthlyRevenue } from "./monthlyRevenue";
import { DataWrapper } from "domain/dataWrapper";
import {
    selectCostReductionOpportunityByStore,
    selectCostsByStores,
    selectReferenceDate,
    selectSelectedStoreByCostType,
    selectSimilarityMetricValues,
    selectSimilarityMetrics,
    selectStoreGroupCorrelations,
    selectCostTypes,
    selectUseCustomCalendar
} from "modules/customer/insights/cost/costSlice";
import { CustomDate } from "modules/customer/insights/cost/customDate";
import { CostMeasureType } from "modules/customer/insights/cost/costMeasureType";
import { CostGranularity } from "modules/customer/insights/cost/costGranularity";
import { setupCube } from "modules/helpers/cube/cubeSlice";
import { openStreetView as mapOpenStreetView } from "modules/helpers/maps/mapsSlice";
import { notifyError } from "modules/notifications/notificationsSlice";

import _ from "lodash";
import { DateTime } from "luxon";
import mathUtils from "utils/mathUtils";
import { SortDirection, numberSortExpression, stringSortExpression } from "utils/sortUtils";
import { ClusterStore } from "./clusterStore";
import { median } from "mathjs";

export enum ClusterStoresSortField {
    StoreName,
    CostAsPercentageOfRevenue,
    CostValue,
    Variance,
    SimilarityScore
}

interface ClusterStoresSort {
    field: ClusterStoresSortField,
    direction: SortDirection
}

interface StoreCostsState {
    isLoading: boolean,
    hasErrors: boolean,
    monthlyCosts: MonthlyCosts[],
    monthlyRevenue: MonthlyRevenue[],
    costMeasure: CostMeasureType,
    costsOverTimeLineChartGranularity: CostGranularity,
    clusterStores: ClusterStore[],
    clusterStoresSort: ClusterStoresSort
}

const initialState: StoreCostsState = {
    isLoading: false,
    hasErrors: false,
    monthlyCosts: [],
    monthlyRevenue: [],
    costMeasure: CostMeasureType.CostValue,
    costsOverTimeLineChartGranularity: CostGranularity.Month,
    clusterStores: [],
    clusterStoresSort: {
        field: ClusterStoresSortField.StoreName,
        direction: SortDirection.ASC
    }
};

interface LoadStoreCostsResponse {
    monthlyCosts: MonthlyCosts[],
    monthlyRevenue: MonthlyRevenue[]
}

const storeCostsSlice = createSlice({
    name: "customer/inisghts/cost/storeCosts",
    initialState,
    reducers: {
        chooseAllClusterStores: (state) => {
            state.clusterStores.forEach(item => item.setIsSelected(true));
        },
        clearCostMeasure: (state) => {
            state.costMeasure = initialState.costMeasure;
        },
        clearClusterStores: (state) => {
            state.clusterStores = initialState.clusterStores;
        },
        clearMonthlyCosts: (state) => {
            state.monthlyCosts = initialState.monthlyCosts;
        },
        clearMonthlyRevenue: (state) => {
            state.monthlyRevenue = initialState.monthlyRevenue;
        },
        clearCostsOverTimeLineChartGranularity: (state) => {
            state.costsOverTimeLineChartGranularity = initialState.costsOverTimeLineChartGranularity;
        },
        deselectAllClusterStores: (state) => {
            state.clusterStores.forEach(item => item.setIsSelected(false));
        },
        setCostMeasure: (state, action: PayloadAction<CostMeasureType>) => {
            state.costMeasure = action.payload;
        },
        setCostsOverTimeLineChartGranularity: (state, action: PayloadAction<CostGranularity>) => {
            state.costsOverTimeLineChartGranularity = action.payload;
        },
        setClusterStores: (state, action: PayloadAction<ClusterStore[]>) => {
            state.clusterStores = action.payload;
        },
        setClusterStoresSort: (state, action: PayloadAction<ClusterStoresSort>) => {
            state.clusterStoresSort = action.payload;
        },
        toggleClusterStoreIsSelected: (state, action: PayloadAction<number | string>) => {
            const id = action.payload;
            state.clusterStores.find(item => item.id === id)?.toggleIsSelected();
        },
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadStoreCosts.pending, (state: StoreCostsState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.monthlyCosts = initialState.monthlyCosts;
            state.monthlyRevenue = initialState.monthlyRevenue;
        });
        builder.addCase(loadStoreCosts.rejected, (state: StoreCostsState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.monthlyCosts = initialState.monthlyCosts;
            state.monthlyRevenue = initialState.monthlyRevenue;
        });
        builder.addCase(loadStoreCosts.fulfilled, (state: StoreCostsState, action: PayloadAction<LoadStoreCostsResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.monthlyCosts = action.payload.monthlyCosts;
            state.monthlyRevenue = action.payload.monthlyRevenue;
        });
    }
});

export const {
    setCostMeasure,
    chooseAllClusterStores,
    clearCostMeasure,
    clearCostsOverTimeLineChartGranularity,
    deselectAllClusterStores,
    setCostsOverTimeLineChartGranularity,
    setClusterStores,
    setClusterStoresSort,
    toggleClusterStoreIsSelected
} = storeCostsSlice.actions;

export const loadStoreCosts = createAppAsyncThunk(
    "customer/insights/cost/storeCosts/loadStoreCosts",
    async (arg, thunkAPI) => {
        try {
            await thunkAPI.dispatch(setupCube());
            const state = thunkAPI.getState();

            const referenceDate = selectReferenceDate(state);
            const useCustomCalendar = selectUseCustomCalendar(state);
            const store = selectSelectedStoreByCostType(state);
            const costsByStore = selectCostsByStores(state);
            const costReductionOpportunityByStore = selectCostReductionOpportunityByStore(state);
            const costsByStores = selectCostsByStores(state);
            const selectedStoreCostReductionOpportunity = selectSelectedStoreByCostType(state);
            const similarStoresIds = costsByStore.data
                .find(item => item.storeId === store?.storeId
                    && item.costName === store.costName
                )?.similarStores
                .map(similarStore => similarStore.id);


            const selectedStoreCosts = costsByStores.data.find(item =>
                item.storeId === selectedStoreCostReductionOpportunity?.storeId
                && item.costName === selectedStoreCostReductionOpportunity.costName
            );

            if (!selectedStoreCostReductionOpportunity || !selectedStoreCosts) {
                return [];
            }

            const clusterStoreIDs = selectedStoreCosts.similarStores.map(item => item.id);

            const clusterStores = costReductionOpportunityByStore.data
                .filter(item => clusterStoreIDs.includes(item.storeId ?? "")
                    && (item.costName === selectedStoreCostReductionOpportunity?.costName))
                .map(clusterStore => {
                    const similarityScore =
                        selectedStoreCosts.similarStores.find(similarStore => similarStore.id === clusterStore.storeId)?.similarityScore ?? 0;
                    return new ClusterStore(
                        clusterStore.storeId,
                        true,
                        clusterStore.storeName ?? "",
                        clusterStore.costAsPercentageOfRevenue,
                        clusterStore.costValue,
                        selectedStoreCostReductionOpportunity?.costValue - clusterStore.costValue,
                        similarityScore
                    );
                })
                .sort((a, b) => numberSortExpression(a.similarityScore, b.similarityScore, SortDirection.DESC));

            thunkAPI.dispatch(setClusterStores(clusterStores));

            const monthlyCostsPromise = thunkAPI.dispatch(
                loadMonthlyCosts(
                    referenceDate,
                    useCustomCalendar,
                    store?.storeId,
                    similarStoresIds,
                    store?.costName
                )
            );

            const monthlyRevenuePromise = thunkAPI.dispatch(
                loadMonthlyRevenue(
                    referenceDate,
                    useCustomCalendar,
                    store?.storeId,
                    similarStoresIds,
                )
            );

            const results = await Promise.all([
                monthlyCostsPromise,
                monthlyRevenuePromise
            ]);
            const monthlyCosts = results[0];
            const monthlyRevenue = results[1];

            const loadCostResponse: LoadStoreCostsResponse = {
                monthlyCosts,
                monthlyRevenue
            };
            return loadCostResponse;
        }
        catch (error) {
            thunkAPI.dispatch(notifyError("Error loading StoreCosts."));
            return thunkAPI.rejectWithValue(null);
        }
    }
);

export const clearStoreCosts = (): AppThunk => (dispatch) => {
    dispatch(storeCostsSlice.actions.clearMonthlyCosts());
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.insights.cost.storeCosts.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.insights.cost.storeCosts.hasErrors;
};

export const selectClusterStores = (state: RootState) => {
    return state.customer.insights.cost.storeCosts.clusterStores;
};

export const selectMonthlyCosts = (state: RootState) => {
    return state.customer.insights.cost.storeCosts.monthlyCosts;
};

export const selectMonthlyRevenue = (state: RootState) => {
    return state.customer.insights.cost.storeCosts.monthlyRevenue;
};

export const selectCostMeasure = (state: RootState) => {
    return state.customer.insights.cost.storeCosts.costMeasure;
};

export const selectCostsOverTimeLineChartGranularity = (state: RootState) => {
    return state.customer.insights.cost.storeCosts.costsOverTimeLineChartGranularity;
};

export const selectClusterStoresSort = (state: RootState) => {
    return state.customer.insights.cost.storeCosts.clusterStoresSort;
};

export const openStreetView = (): AppThunk => (dispatch, getState) => {
    const state = getState();
    const selectedStore = selectSelectedStoreByCostType(state);
    const latitude = selectedStore?.latitude ?? 0;
    const longitude = selectedStore?.longitude ?? 0;
    dispatch(mapOpenStreetView(latitude, longitude));
};

export const selectStoreVsComparatorPerformance = createSelector(
    selectIsLoading,
    selectHasErrors,
    (state: RootState) => selectSelectedStoreByCostType(state),
    (state: RootState) => selectClusterStores(state),
    (state: RootState) => selectCostsByStores(state),
    (state: RootState) => selectSimilarityMetricValues(state),
    (state: RootState) => selectSimilarityMetrics(state),
    (state: RootState) => selectStoreGroupCorrelations(state),
    (state: RootState) => selectCostTypes(state),
    (storeCostsIsLoading, storeCostsHasErrors, store, clusterStores, costsByStores, metricValues, metrics, correlations, costTypes) => {
        interface StoreVsComparatorPerformanceMetrics {
            metricNameId: number,
            metricName: string,
            metricValue: number
        }

        interface StoreVsComparatorPerformance {
            store: StoreVsComparatorPerformanceMetrics[],
            clusterAverage: StoreVsComparatorPerformanceMetrics[]
        }

        const isLoading = costsByStores.isLoading || metricValues.isLoading || correlations.isLoading || storeCostsIsLoading;
        const hasErrors = costsByStores.hasErrors || metricValues.hasErrors || correlations.hasErrors || storeCostsHasErrors;

        const storeVsComparatorPerformance: DataWrapper<StoreVsComparatorPerformance> = {
            isLoading,
            hasErrors,
            data: {
                store: [],
                clusterAverage: []
            }
        };

        if (!store || storeVsComparatorPerformance.isLoading || storeVsComparatorPerformance.hasErrors) {
            return storeVsComparatorPerformance;
        }

        const selectedStoreCostType = costTypes.find(costType => costType.name === store.costName);

        // Store metric values
        const orderedCorrelations = correlations.data
            .filter(correlation => correlation.costTypeId === Number(selectedStoreCostType?.id)
                && correlation.storeGroup === store.group
            )
            .map(correlation => {
                return {
                    metricNameId: correlation.metricNameId,
                    correlationValue: Math.abs(correlation.correlationValue)
                };
            })
            .sort((a, b) => numberSortExpression(Math.abs(a.correlationValue), Math.abs(b.correlationValue), SortDirection.DESC))
            .slice(0, 6);

        const topSixMetricNameIds = orderedCorrelations.map(oc => oc.metricNameId);

        const storeMetricValues = metricValues.data
            .filter(mv => mv.storeId === store.storeId && topSixMetricNameIds.includes(mv.metricNameId))
            .map(mv => {
                const metric = metrics.find(metric => metric.id === mv.metricNameId);
                const correlationValue = orderedCorrelations.find(oc => oc.metricNameId === Number(metric?.id))?.correlationValue;

                return {
                    metricNameId: metric?.id ?? 0,
                    metricName: metric?.name ?? "",
                    metricValue: mv.metricValue,
                    correlationValue: correlationValue ?? 0
                };
            });

        storeVsComparatorPerformance.data.store = storeMetricValues
            .sort((a, b) => numberSortExpression(Math.abs(a.correlationValue), Math.abs(b.correlationValue), SortDirection.DESC));

        // Cluster average metric values
        const similarStoreIds = clusterStores
            .filter(store => store.isSelected())
            .map(store => store.id);

        const clusterMetricValues = metricValues.data
            .filter(mv => similarStoreIds?.includes(mv.storeId) && topSixMetricNameIds.includes(mv.metricNameId))
            .map(mv => {
                const metric = metrics.find(metric => metric.id === mv.metricNameId);
                return {
                    metricNameId: metric?.id ?? 0,
                    metricName: metric?.name ?? "",
                    metricValue: mv.metricValue
                };
            });

        const averageClusterMetricValues = topSixMetricNameIds.map((metric) => {
            const similarityMetric = metrics.find(m => m.id === metric);
            const clusterMetric = clusterMetricValues.filter(clusterMetricValue => clusterMetricValue.metricNameId === metric);
            return {
                metricNameId: similarityMetric?.id ?? 0,
                metricName: similarityMetric?.name ?? "",
                metricValue: mathUtils.safeMedian(clusterMetric.map(x => x.metricValue)) ?? 0
            };
        });

        storeVsComparatorPerformance.data.clusterAverage = averageClusterMetricValues;

        return storeVsComparatorPerformance;
    }
);

export const selectStoreCostsAndOpportunity = createSelector(
    selectSelectedStoreByCostType,
    selectClusterStores,
    (store, clusterStores) => {
        const selectedClusterStores = clusterStores.filter(item => item.isSelected());
        if (!store || selectedClusterStores.length === 0) {
            return undefined;
        }

        const similarStoresAverageCostValue = median(selectedClusterStores.map(item => item.costValue));
        const similarStoresAverageCostPercentageOfRevenue = median(selectedClusterStores.map(item => item.costAsPercentageOfRevenue));

        const currentStoreCostAsPercentage = mathUtils.safePercentage(store.costValue, (store.revenue ?? 0)) / 100;
        const opportunityValueAsPercentageOfRevenue = currentStoreCostAsPercentage - similarStoresAverageCostPercentageOfRevenue;
        const opportunityValue = opportunityValueAsPercentageOfRevenue * (store?.revenue ?? 0);

        return {
            opportunity: {
                value: opportunityValue,
                percentageOfRevenue: opportunityValueAsPercentageOfRevenue
            },
            clusterMedianCosts: {
                value: similarStoresAverageCostValue,
                percentageOfRevenue: similarStoresAverageCostPercentageOfRevenue
            }
        };
    }
);

export const selectCostsOverTime = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectSelectedStoreByCostType,
    selectClusterStores,
    selectMonthlyCosts,
    selectMonthlyRevenue,
    selectCostsOverTimeLineChartGranularity,
    (
        isLoading,
        hasErrors,
        store,
        clusterStores,
        monthlyCosts,
        monthlyRevenue,
        costsOverTimeLineChartGranularity
    ) => {
        interface StoreCost {
            date: DateTime,
            customDate: CustomDate,
            costValue: number,
            costAsPercentageOfRevenue: number
        }

        interface StoreCostOverTime {
            store: StoreCost[]
            clusterAverage: StoreCost[]
        }

        const storeCostsOverTime: DataWrapper<StoreCostOverTime> = {
            isLoading: isLoading,
            hasErrors: hasErrors,
            data: {
                store: [],
                clusterAverage: []
            }
        };

        if (!store || !monthlyCosts || !monthlyRevenue) {
            return storeCostsOverTime;
        }

        const clusterStoresIds = clusterStores
            .filter(store => store.isSelected())
            .map(store => store.id);

        storeCostsOverTime.data.store = _(monthlyCosts)
            .filter(costs => costs.storeId === store.storeId)
            .groupBy(costs => costs.customDate[costsOverTimeLineChartGranularity])
            .map((monthlyCosts, monthKey) => {
                const earliestItem = _.minBy(monthlyCosts, item => item.date.toMillis()) ?? monthlyCosts[0];
                const revenue = monthlyRevenue.filter(item =>
                    item.storeId === store.storeId && String(item.customDate[costsOverTimeLineChartGranularity]) === monthKey);
                const totalCosts = monthlyCosts.reduce((total, item) => item.costValue + total, 0);
                const totalRevenue = revenue?.reduce((total, item) => item.revenue + total, 0);
                return {
                    date: earliestItem.date,
                    customDate: earliestItem.customDate,
                    costValue: totalCosts,
                    costAsPercentageOfRevenue: mathUtils.safePercentage(totalCosts, totalRevenue)
                };
            })
            .sort((a, b) => numberSortExpression(a.customDate.date.toMillis(), b.customDate.date.toMillis(), SortDirection.ASC))
            .value();

        storeCostsOverTime.data.clusterAverage = _(monthlyCosts)
            .filter(costs => clusterStoresIds.includes(costs.storeId))
            .groupBy(costs => costs.customDate[costsOverTimeLineChartGranularity])
            .map((monthlyCosts, monthKey) => {
                const earliestItem = _.minBy(monthlyCosts, item => item.date.toMillis()) ?? monthlyCosts[0];
                const revenue = monthlyRevenue.filter(item =>
                    item.storeId !== store.storeId && String(item.customDate[costsOverTimeLineChartGranularity]) === monthKey);

                const groupedCosts = _(monthlyCosts).groupBy(cost => cost.storeId).value();
                const groupedRevenue = _(revenue).groupBy(revenue => revenue.storeId).value();

                const storeYearData = _(groupedCosts).map((group, storeKey) => {
                    const revenue = groupedRevenue[storeKey];
                    const totalCosts = group.reduce((total, item) => item.costValue + total, 0);
                    const totalRevenue = (revenue !== undefined ? revenue.reduce((total, item) => item.revenue + total, 0) : 0);

                    return {
                        cost: totalCosts,
                        revenue: totalRevenue,
                        costPercentageOfRevenue: mathUtils.safePercentage(totalCosts, totalRevenue)
                    };
                }).value();

                const medianCostValue = mathUtils.safeMedian(storeYearData.map(data => data.cost)) ?? 0;
                const medianCostPercentageOfRevenue = mathUtils.safeMedian(storeYearData.map(data => data.costPercentageOfRevenue)) ?? 0;

                return {
                    key: monthKey,
                    date: earliestItem.date,
                    customDate: earliestItem.customDate,
                    costValue: medianCostValue,
                    costAsPercentageOfRevenue: medianCostPercentageOfRevenue
                };
            })
            .sort((a, b) => numberSortExpression(a.customDate.date.toMillis(), b.customDate.date.toMillis(), SortDirection.ASC))
            .value();

        return storeCostsOverTime;
    }
);

export const selectSortedClusterStores = createSelector(
    selectClusterStores,
    selectClusterStoresSort,
    (clusterStores, sort) => {
        if (!clusterStores) {
            return [];
        }

        return clusterStores
            .filter(store => store.isSelected())
            .slice()
            .sort((a, b) => {
                switch (sort.field) {
                    case ClusterStoresSortField.StoreName:
                        return stringSortExpression(a.name, b.name, sort.direction);
                    case ClusterStoresSortField.CostAsPercentageOfRevenue:
                        return numberSortExpression(a.costAsPercentageOfRevenue, b.costAsPercentageOfRevenue, sort.direction);
                    case ClusterStoresSortField.CostValue:
                        return numberSortExpression(a.costValue, b.costValue, sort.direction);
                    case ClusterStoresSortField.Variance:
                        return numberSortExpression(a.variance, b.variance, sort.direction);
                    case ClusterStoresSortField.SimilarityScore:
                        return numberSortExpression(a.similarityScore ?? 0, b.similarityScore ?? 0, sort.direction);
                    default:
                        return stringSortExpression(a.name, b.name, SortDirection.ASC);
                }
            });
    }
);

export default storeCostsSlice;
